import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import SectionHeader from "../SectionHeader";
import { FaUserNurse, FaUserTie, FaRunning, FaUserFriends } from "react-icons/fa";
import { MdFamilyRestroom, MdOutlineSelfImprovement } from "react-icons/md";
import {
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    Activity,
    Zap,
    Flame,
    HeartPulse,
    ShieldCheck,
} from "lucide-react";

type PersonaProps = {
    key: string;
    icon: React.ReactNode;
    title: string;
    description: string;
    iconBg: string;
    index: number;
    details: string[];
};

const UserPersonas: React.FC = () => {
    const { t, i18n } = useTranslation("UserPersonas");
    const isRTL = ["fa", "ar"].includes(i18n.language);
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

    const toggleExpand = (index: number) => {
        setExpandedIndex((prev) => (prev === index ? null : index));
    };

    const personas: PersonaProps[] = [
        {
            key: "parents",
            icon: <MdFamilyRestroom className="text-2xl text-[#0072CE]" />,
            title: t("personas.parents.title"),
            description: t("personas.parents.description"),
            iconBg: "bg-blue-100/80 border border-blue-200/60",
            index: 0,
            details: [
                t("personas.parents.details.0"),
                t("personas.parents.details.1"),
                t("personas.parents.details.2"),
                t("personas.parents.details.3"),
                t("personas.parents.details.4"),
            ],
        },
        {
            key: "caregivers",
            icon: <FaUserNurse className="text-2xl text-indigo-600" />,
            title: t("personas.caregivers.title"),
            description: t("personas.caregivers.description"),
            iconBg: "bg-indigo-100/80 border border-indigo-200/60",
            index: 1,
            details: [
                t("personas.caregivers.details.0"),
                t("personas.caregivers.details.1"),
                t("personas.caregivers.details.2"),
                t("personas.caregivers.details.3"),
                t("personas.caregivers.details.4"),
            ],
        },
        {
            key: "hrManagers",
            icon: <FaUserTie className="text-2xl text-sky-600" />,
            title: t("personas.hrManagers.title"),
            description: t("personas.hrManagers.description"),
            iconBg: "bg-sky-100/80 border border-sky-200/60",
            index: 2,
            details: [
                t("personas.hrManagers.details.0"),
                t("personas.hrManagers.details.1"),
                t("personas.hrManagers.details.2"),
                t("personas.hrManagers.details.3"),
                t("personas.hrManagers.details.4"),
            ],
        },
        {
            key: "healthEnthusiasts",
            icon: <MdOutlineSelfImprovement className="text-2xl text-cyan-600" />,
            title: t("personas.healthEnthusiasts.title"),
            description: t("personas.healthEnthusiasts.description"),
            iconBg: "bg-cyan-100/80 border border-cyan-200/60",
            index: 3,
            details: [
                t("personas.healthEnthusiasts.details.0"),
                t("personas.healthEnthusiasts.details.1"),
                t("personas.healthEnthusiasts.details.2"),
                t("personas.healthEnthusiasts.details.3"),
                t("personas.healthEnthusiasts.details.4"),
            ],
        },
        {
            key: "couples",
            icon: <FaUserFriends className="text-2xl text-blue-700" />,
            title: t("personas.couples.title"),
            description: t("personas.couples.description"),
            iconBg: "bg-blue-100/80 border border-blue-200/60",
            index: 4,
            details: [
                t("personas.couples.details.0"),
                t("personas.couples.details.1"),
                t("personas.couples.details.2"),
                t("personas.couples.details.3"),
                t("personas.couples.details.4"),
            ],
        },
        {
            key: "athletes",
            icon: <FaRunning className="text-2xl text-[#0038A8]" />,
            title: t("personas.athletes.title"),
            description: t("personas.athletes.description"),
            iconBg: "bg-blue-100/80 border border-blue-200/60",
            index: 5,
            details: [
                t("personas.athletes.details.0"),
                t("personas.athletes.details.1"),
                t("personas.athletes.details.2"),
                t("personas.athletes.details.3"),
                t("personas.athletes.details.4"),
            ],
        },
    ];

    const athleteBulletIcons = [
        <Activity className="w-4 h-4 text-blue-600" />,
        <Zap className="w-4 h-4 text-amber-500" />,
        <Flame className="w-4 h-4 text-rose-500" />,
        <HeartPulse className="w-4 h-4 text-cyan-600" />,
        <ShieldCheck className="w-4 h-4 text-emerald-600" />,
    ];

    const athleteBulletBgs = [
        "from-blue-50/90 to-blue-100/40 border-blue-200/80",
        "from-amber-50/90 to-amber-100/30 border-amber-200/80",
        "from-rose-50/90 to-rose-100/30 border-rose-200/80",
        "from-cyan-50/90 to-cyan-100/30 border-cyan-200/80",
        "from-emerald-50/90 to-emerald-100/30 border-emerald-200/80",
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.08,
            },
        },
    };

    const itemVariants: any = {
        hidden: { opacity: 0, y: 16 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.4,
                ease: "easeOut",
            },
        },
    };

    const renderPersonaCard = (persona: PersonaProps, colSpan: string) => {
        const isExpanded = expandedIndex === persona.index;

        return (
            <motion.div
                key={persona.index}
                variants={itemVariants}
                onClick={() => toggleExpand(persona.index)}
                className={`group relative ${colSpan} rounded-2xl p-6 sm:p-7 bg-white/90 backdrop-blur-md border border-slate-200/80 shadow-sm hover:shadow-lg hover:border-[#0072CE]/40 transition-all duration-300 cursor-pointer flex flex-col justify-between overflow-hidden`}
                whileHover={{ y: -2, transition: { duration: 0.2 } }}
            >
                {/* Top Border Accent */}
                <div
                    className={`absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-[#0038A8] via-[#0072CE] to-sky-400 transition-opacity duration-300 ${
                        isExpanded ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                    }`}
                />

                <div className="relative z-10 flex flex-col h-full">
                    {/* Icon Header */}
                    <div className="flex items-center justify-between mb-4">
                        <div
                            className={`w-14 h-14 rounded-2xl ${persona.iconBg} flex items-center justify-center text-2xl group-hover:scale-105 transition-transform duration-300`}
                        >
                            {persona.icon}
                        </div>
                    </div>

                    {/* Title & Description */}
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 text-start group-hover:text-[#0072CE] transition-colors">
                        {persona.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed text-start mb-4 flex-grow">
                        {persona.description}
                    </p>

                    {/* Action CTA Button */}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100 mt-auto">
                        <span className="text-sm font-semibold text-[#0072CE] group-hover:text-[#0038A8] transition-colors flex items-center gap-1.5">
                            {isExpanded
                                ? t("common.closeDetails", { defaultValue: "بستن جزئیات" })
                                : t("common.viewDetails", { defaultValue: "مشاهده نحوه کارکرد" })}
                        </span>
                        <div className="w-7 h-7 rounded-full bg-blue-50 text-[#0072CE] flex items-center justify-center group-hover:bg-[#0072CE] group-hover:text-white transition-colors">
                            {isExpanded ? (
                                <ChevronUp className="w-4 h-4" />
                            ) : (
                                <ChevronDown className="w-4 h-4" />
                            )}
                        </div>
                    </div>

                    {/* Inline Expandable Details Content */}
                    <AnimatePresence>
                        {isExpanded && (
                            <motion.div
                                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                animate={{ opacity: 1, height: "auto", marginTop: 16 }}
                                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                                className="overflow-hidden border-t border-blue-100/80 pt-4"
                            >
                                <div className="space-y-2.5">
                                    {persona.details.map((detail, idx) => (
                                        <motion.div
                                            key={idx}
                                            initial={{ opacity: 0, y: 6 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: idx * 0.04 }}
                                            className="p-3 rounded-xl bg-gradient-to-r from-blue-50/80 via-blue-50/40 to-white border border-blue-100/80 flex items-start gap-2.5"
                                        >
                                            <div className="w-5 h-5 rounded-md bg-[#0072CE]/10 text-[#0072CE] flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <CheckCircle2 className="w-3.5 h-3.5" />
                                            </div>
                                            <span className="text-xs sm:text-sm text-slate-700 font-medium leading-relaxed text-start">
                                                {detail}
                                            </span>
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>
        );
    };

    const isAthleteExpanded = expandedIndex === 5;

    return (
        <section className="py-12" id="who-uses" dir={isRTL ? "rtl" : "ltr"}>
            <div className="container mx-auto pb-8">
                <div className="text-center">
                    <SectionHeader
                        regularText={t("header.regularText")}
                        highlightedText={t("header.highlightedText")}
                        descriptionHighlight={t("header.descriptionHighlight")}
                        descriptionText={t("header.descriptionText")}
                    />
                </div>
            </div>

            <div className="container mx-auto px-4">
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-80px" }}
                    className="grid grid-cols-1 md:grid-cols-12 gap-6"
                >
                    {/* First row - 2 cards */}
                    {renderPersonaCard(personas[0], "md:col-span-7")}
                    {renderPersonaCard(personas[1], "md:col-span-5")}

                    {/* Second row - 3 cards */}
                    {renderPersonaCard(personas[2], "md:col-span-4")}
                    {renderPersonaCard(personas[3], "md:col-span-4")}
                    {renderPersonaCard(personas[4], "md:col-span-4")}

                    {/* Third row - Athlete Wide Card */}
                    <motion.div
                        variants={itemVariants}
                        onClick={() => toggleExpand(5)}
                        className="group relative md:col-span-12 rounded-2xl p-6 sm:p-8 bg-gradient-to-br from-white via-blue-50/30 to-white backdrop-blur-md border border-blue-200/80 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden"
                        whileHover={{ y: -2, transition: { duration: 0.2 } }}
                    >
                        <div
                            className={`absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-[#0038A8] via-[#0072CE] to-sky-400 transition-opacity duration-300 ${
                                isAthleteExpanded ? "opacity-100" : "opacity-80 group-hover:opacity-100"
                            }`}
                        />

                        <div className="relative z-10 flex flex-col md:flex-row md:items-start justify-between gap-6">
                            {/* Left Section: Icon, Title, Description */}
                            <div className="md:w-5/12 text-start flex flex-col justify-between">
                                <div>
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0038A8] to-[#0072CE] text-white flex items-center justify-center text-2xl shadow-md shadow-blue-500/20 group-hover:scale-105 transition-transform duration-300">
                                            {personas[5].icon}
                                        </div>
                                    </div>
                                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 group-hover:text-[#0072CE] transition-colors">
                                        {personas[5].title}
                                    </h3>
                                    <p className="text-slate-600 text-sm leading-relaxed mb-4">
                                        {personas[5].description}
                                    </p>
                                </div>

                                <div className="inline-flex items-center gap-2 text-sm font-semibold text-[#0072CE] group-hover:text-[#0038A8] transition-colors pt-2">
                                    <span>
                                        {isAthleteExpanded
                                            ? t("common.closeDetails", { defaultValue: "بستن جزئیات" })
                                            : t("common.viewDetails", { defaultValue: "مشاهده نحوه کارکرد" })}
                                    </span>
                                    <div className="w-6 h-6 rounded-full bg-blue-100 text-[#0072CE] flex items-center justify-center">
                                        {isAthleteExpanded ? (
                                            <ChevronUp className="w-4 h-4" />
                                        ) : (
                                            <ChevronDown className="w-4 h-4" />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Right Section: Enhanced Athlete Bullet Points */}
                            <div className="md:w-7/12 border-t md:border-t-0 md:border-s border-blue-100 pt-4 md:pt-0 md:ps-8">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {(isAthleteExpanded
                                        ? personas[5].details
                                        : personas[5].details.slice(0, 4)
                                    ).map((detail, idx) => (
                                        <motion.div
                                            key={idx}
                                            initial={{ opacity: 0, scale: 0.96 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ duration: 0.2, delay: idx * 0.03 }}
                                            className={`p-3.5 rounded-xl bg-gradient-to-br ${
                                                athleteBulletBgs[idx % athleteBulletBgs.length]
                                            } border flex items-start gap-3 shadow-xs hover:shadow-md transition-all duration-200`}
                                        >
                                            <div className="w-8 h-8 rounded-lg bg-white shadow-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                                                {athleteBulletIcons[idx % athleteBulletIcons.length]}
                                            </div>
                                            <span className="text-xs sm:text-sm text-slate-800 font-semibold leading-snug text-start self-center">
                                                {detail}
                                            </span>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

export default UserPersonas;
